import { cn } from "@/lib/utils";
import { motion, useReducedMotion } from "framer-motion";

interface AnimatedCheckmarkProps {
  /** Size of the checkmark circle */
  size?: number;
  /** Stroke width */
  strokeWidth?: number;
  /** Additional className for the container */
  className?: string;
  /** Whether to use bank theme color (default) or custom color */
  useBankColor?: boolean;
  /** Custom color override (hex) */
  color?: string;
  /** Delay before animation starts */
  delay?: number;
}

/**
 * Animated checkmark with draw-in effect.
 * Used for success states with a satisfying reveal animation.
 */
export const AnimatedCheckmark: React.FC<AnimatedCheckmarkProps> = ({
  size = 80,
  strokeWidth = 4,
  className,
  useBankColor = true,
  color,
  delay = 0,
}) => {
  const shouldReduceMotion = useReducedMotion();

  const checkColor =
    color || (useBankColor ? "var(--bank-primary-hex)" : "#22C55E");

  // SVG path for the checkmark - centered in 60x60 viewBox
  const checkmarkPath = "M18 30 L26 38 L42 22";

  // Circle properties
  const center = size / 2;
  const radius = (size - strokeWidth) / 2 - 4;
  const circumference = 2 * Math.PI * radius;

  if (shouldReduceMotion) {
    // Static version for reduced motion
    return (
      <div
        className={cn("relative", className)}
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          fill="none"
          className="transform -rotate-90"
        >
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={checkColor}
            strokeWidth={strokeWidth}
            fill="none"
            opacity={0.2}
          />
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={checkColor}
            strokeWidth={strokeWidth}
            fill="none"
          />
        </svg>
        <svg
          width={size}
          height={size}
          viewBox="0 0 60 60"
          fill="none"
          className="absolute inset-0"
        >
          <path
            d={checkmarkPath}
            stroke={checkColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </div>
    );
  }

  return (
    <div
      className={cn("relative", className)}
      style={{ width: size, height: size }}
    >
      {/* Background circle (faded) */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        className="absolute inset-0"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={checkColor}
          strokeWidth={strokeWidth}
          fill="none"
          opacity={0.15}
        />
      </svg>

      {/* Animated circle */}
      <motion.svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        className="absolute inset-0 transform -rotate-90"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay, duration: 0.2 }}
      >
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          stroke={checkColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          initial={{
            strokeDasharray: circumference,
            strokeDashoffset: circumference,
          }}
          animate={{ strokeDashoffset: 0 }}
          transition={{
            delay: delay + 0.1,
            duration: 0.5,
            ease: [0.25, 1, 0.5, 1],
          }}
        />
      </motion.svg>

      {/* Animated checkmark */}
      <motion.svg
        width={size}
        height={size}
        viewBox="0 0 60 60"
        fill="none"
        className="absolute inset-0"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: delay + 0.3,
          duration: 0.4,
          ease: [0.25, 1, 0.5, 1],
        }}
      >
        <motion.path
          d={checkmarkPath}
          stroke={checkColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{
            delay: delay + 0.4,
            duration: 0.3,
            ease: [0.25, 1, 0.5, 1],
          }}
        />
      </motion.svg>
    </div>
  );
};

/**
 * Simple static checkmark icon (for inline use)
 */
export const CheckIcon: React.FC<{
  className?: string;
  size?: number;
}> = ({ className, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    className={className}
  >
    <path
      d="M4 10.5L8 14.5L16 6.5"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default AnimatedCheckmark;
